import React, { useEffect, useState } from "react";
import { Steps, Spin, message } from "antd";
import { v4 as uuidv4 } from "uuid";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { uploadFileToS3 } from "../../../utils/s3Upload";
import { extractBidOptimizerInput } from "../utils/engineDataExtraction";
import StepUpload, { StepUploadData } from "./StepUpload";
import StepConfiguration, { StepConfigurationData } from "./StepConfiguration";
import StepProcessing from "./StepProcessing";
import StepPowerAnalysis from "./StepPowerAnalysis";
import StepApplyBid from "./StepApplyBid";
import StepIncrementalityResults from "./StepIncrementalityResults";

// Internal step indices (0-5) vs. the wireframe's 5 rail steps: Processing (2) is
// an interstitial, not a rail step of its own - it shares the rail's "Power
// Analysis" slot while the job runs. See causal-loop-feature/plan.md.
const RAIL_STEPS = [
  { title: "Upload Data", description: "1 file - powers both engines" },
  { title: "Configuration", description: "Bid Optimizer + Geo Test setup" },
  { title: "Power Analysis", description: "Picks your Test market" },
  { title: "Apply Bid", description: "Select rows, apply to campaigns" },
  { title: "Incrementality Results", description: "Measured lift vs Control" },
];

const railIndexFor = (current: number) => (current <= 1 ? current : current - 1);

const CreateCausalLoop: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [current, setCurrent] = useState<number>(location?.state?.step ?? 0);
  const [stepUploadData, setStepUploadData] = useState<StepUploadData | null>(null);
  const [stepConfigurationData, setStepConfigurationData] = useState<StepConfigurationData | null>(null);
  const [jobName, setJobName] = useState<string>(location?.state?.jobName || "");
  const [jobConfigId, setJobConfigId] = useState<string>(location?.state?.jobConfigId || "");
  const [resumedParams, setResumedParams] = useState<{
    isSales: boolean;
  } | null>(null);
  const [uploadingEarly, setUploadingEarly] = useState(false);
  // Set when opened via "View" from the job list - the job already exists and
  // already ran, so Step 2 is shown read-only from its saved config (no file
  // in this browser session to re-detect columns from) and Next skips the
  // Processing interstitial (that only ever submits a job once, at creation).
  const viewOnly: boolean = Boolean(location?.state?.viewOnly);
  const [viewConfigData, setViewConfigData] = useState<StepConfigurationData | null>(null);

  useEffect(() => {
    if (!viewOnly || !jobConfigId || viewConfigData) return;
    axiosInstance
      .get(`/api/jobConfig/${jobConfigId}`)
      .then((res) => {
        const cfg = res.data || {};
        const bm = cfg.dataMappingConfig?.mappings?.bidMultiplier?.columnMapping || {};
        const gl = cfg.dataMappingConfig?.mappings?.geoLift?.columnMapping || {};
        const params = cfg.modelParameterConfig?.parameters || {};
        const mapped: StepConfigurationData = {
          jobName: cfg.name || jobName || "",
          campaignObjective: params.campaignObjective || "sales",
          customerIdentifier: "",
          targetVariable: bm.target_variables?.[0] || gl.kpi_variables?.[0] || "",
          age: bm.age_varialbes?.[0],
          gender: bm.gender_varialbes?.[0],
          devicePlatform: bm.device_platform_varialbes?.[0],
          geoLocationType: params.geoLocationType || "dma",
          region: bm.region_varialbes?.[0] || gl.location_variables?.[0] || "",
          locale: bm.locale_varialbes?.[0],
          userOs: bm.user_os_varialbes?.[0],
          dateColumn: gl.date_variables?.[0] || "",
          campaignStartDate: params.campaignStartDate || "",
          bauSpent: params.bauSpent ?? 0,
          statSigThreshold: params.statSigThreshold ?? 90,
          scmType: params.scmType || "single",
          isSales: params.isSales ?? true,
          selectedMarkets: params.includeMarket || [],
        };
        setViewConfigData(mapped);
        setStepConfigurationData(mapped);
        if (!jobName) setJobName(mapped.jobName);
      })
      .catch((err) => {
        // Without a fallback here, a failed fetch leaves viewConfigData null
        // forever, and StepConfiguration's "existingData not ready yet" guard
        // shows a permanent loading spinner with no way to proceed - set a
        // best-effort empty shape instead so the step still renders (fields
        // will show blank rather than their saved values, but Next stays usable).
        console.error("Error loading saved Causal Loop configuration:", err);
        const fallback: StepConfigurationData = {
          jobName: jobName || "",
          campaignObjective: "sales",
          customerIdentifier: "",
          targetVariable: "",
          geoLocationType: "dma",
          region: "",
          dateColumn: "",
          campaignStartDate: "",
          bauSpent: 0,
          statSigThreshold: 90,
          scmType: "single",
          isSales: true,
          selectedMarkets: [],
        };
        setViewConfigData(fallback);
        setStepConfigurationData(fallback);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewOnly, jobConfigId]);

  // Only the Bid Optimizer copy uploads here, right after Step 1 - it needs no
  // transformation (extractBidOptimizerInput is an identity function). The Geo
  // Lift copy is NOT uploaded yet: it needs date/location/target columns to be
  // known first so it can be properly aggregated to one row per location per
  // date (extractSyntheticControlInput, in StepConfiguration.tsx) - GeoLift's
  // existing code assumes that shape everywhere and silently miscomputes
  // (rather than erroring) when a file has multiple rows per location per date,
  // which Causal Loop's shared file always does before that column mapping is
  // known. See causal-loop-feature/plan.md.
  const handleUploadNext = async (data: StepUploadData) => {
    setStepUploadData(data);
    if (jobConfigId) {
      // Resuming with an id already known (shouldn't normally hit this step, but
      // guards against re-uploading under a stale id).
      setCurrent(1);
      return;
    }
    setUploadingEarly(true);
    try {
      const newJobId = uuidv4();
      await uploadFileToS3(
        extractBidOptimizerInput(data.dataFile),
        `${newJobId}/input/bid_multiplier_data.csv`
      );
      setJobConfigId(newJobId);
      setCurrent(1);
    } catch (err) {
      console.error("Error uploading Causal Loop data:", err);
      message.error("Failed to upload your file. Please try again.");
    } finally {
      setUploadingEarly(false);
    }
  };

  // Resuming an existing job (clicked "View" from the job list) lands directly on
  // a results step with only jobConfigId/jobName in location.state - Step 2's
  // local data never existed in this session, so the later steps' small set of
  // needed params (bauSpent/statSigThreshold/isSales) are re-fetched here.
  useEffect(() => {
    if (jobConfigId && !stepConfigurationData && current >= 3 && !resumedParams) {
      axiosInstance
        .get(`/api/jobConfig/${jobConfigId}`)
        .then((res) => {
          const params = res.data?.modelParameterConfig?.parameters || {};
          setResumedParams({
            isSales: params.isSales ?? true,
          });
          if (!jobName) setJobName(res.data?.name || "");
        })
        .catch(() => {
          setResumedParams({ isSales: true });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobConfigId, current]);

  const effectiveIsSales = stepConfigurationData?.isSales ?? resumedParams?.isSales ?? true;

  // View-mode-only read-only summary for Step 0 (Upload) - there's no file in
  // this browser session to re-preview, so this shows which columns were
  // mapped instead, derived from the same fetched job config Step 1 uses.
  const viewColumns = viewConfigData
    ? ([
        { label: "Date Column", value: viewConfigData.dateColumn },
        { label: "Region / Geo Location", value: viewConfigData.region },
        { label: "Target Variable", value: viewConfigData.targetVariable },
        { label: "Age Range", value: viewConfigData.age },
        { label: "Gender", value: viewConfigData.gender },
        { label: "Device Platform", value: viewConfigData.devicePlatform },
        { label: "Locale", value: viewConfigData.locale },
        { label: "User OS", value: viewConfigData.userOs },
      ].filter((c) => c.value) as { label: string; value: string }[])
    : [];

  const getStepContent = () => {
    if (uploadingEarly) {
      return (
        <div className="text-center py-24">
          <Spin size="large" tip="Uploading your data..." />
        </div>
      );
    }
    switch (current) {
      case 0:
        if (viewOnly && jobConfigId) {
          if (!viewConfigData) {
            return (
              <div className="text-center py-24">
                <Spin size="large" tip="Loading saved configuration..." />
              </div>
            );
          }
          return (
            <StepUpload
              onNext={handleUploadNext}
              viewMode
              viewColumns={viewColumns}
              onPrevious={() => navigate("/causal-loop/list")}
              onViewNext={() => setCurrent(1)}
            />
          );
        }
        return <StepUpload onNext={handleUploadNext} existingData={stepUploadData} />;
      case 1:
        if (viewOnly && jobConfigId) {
          return (
            <StepConfiguration
              stepUploadData={stepUploadData}
              jobConfigId={jobConfigId}
              viewMode
              onNext={(data) => {
                setStepConfigurationData(data);
                setCurrent(3);
              }}
              onPrevious={() => setCurrent(0)}
              onNameChange={setJobName}
              existingData={viewConfigData}
            />
          );
        }
        return stepUploadData && jobConfigId ? (
          <StepConfiguration
            stepUploadData={stepUploadData}
            jobConfigId={jobConfigId}
            onNext={(data) => {
              setStepConfigurationData(data);
              setCurrent(2);
            }}
            onPrevious={() => setCurrent(0)}
            onNameChange={setJobName}
            existingData={stepConfigurationData}
          />
        ) : (
          <div className="text-center p-8 text-gray-500">Please complete Step 1 first.</div>
        );
      case 2:
        return stepUploadData && stepConfigurationData && jobConfigId ? (
          <StepProcessing
            jobConfigId={jobConfigId}
            stepConfigurationData={stepConfigurationData}
            onSuccess={() => setCurrent(3)}
          />
        ) : (
          <div className="text-center p-8 text-gray-500">Please complete previous steps first.</div>
        );
      case 3:
        return jobConfigId ? (
          <StepPowerAnalysis
            jobConfigId={jobConfigId}
            jobName={jobName}
            isSales={effectiveIsSales}
            onPrevious={() => (viewOnly || stepUploadData ? setCurrent(1) : navigate("/causal-loop/list"))}
            onNext={() => setCurrent(4)}
          />
        ) : (
          <div className="text-center p-8 text-gray-500">Please complete previous steps first.</div>
        );
      case 4:
        return jobConfigId ? (
          <StepApplyBid
            jobConfigId={jobConfigId}
            onPrevious={() => setCurrent(3)}
            onNext={() => setCurrent(5)}
          />
        ) : null;
      case 5:
        return jobConfigId ? (
          <StepIncrementalityResults
            jobConfigId={jobConfigId}
            isSales={effectiveIsSales}
            onPrevious={() => setCurrent(4)}
          />
        ) : null;
      default:
        return null;
    }
  };

  const railCurrent = railIndexFor(current);

  return (
    <div className="w-full h-[calc(100vh-75px)] flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="flex flex-1 h-full overflow-hidden">
        <div className="max-h-[calc(100vh)] overflow-y-auto w-1/4 px-8 py-4 bg-gray-50 border-r">
          <div className="pb-1 pt-2 bg-gray-50 mb-4 px-4">
            <h2
              title={jobName || "Causal Loop Study"}
              className="text-2xl font-semibold text-gray-900 max-w-[350px] truncate"
            >
              {jobName || "Causal Loop Study"}
            </h2>
          </div>
          <Steps
            direction="vertical"
            current={railCurrent}
            items={RAIL_STEPS.map((step, index) => ({
              title: step.title,
              description: current === 2 && index === 2 ? "Running..." : step.description,
              disabled: true,
              status:
                index < railCurrent ? "finish" : index === railCurrent ? "process" : "wait",
            }))}
            className="[&_.ant-steps-item]:mb-16 [&_.ant-steps-item-tail]:!top-3 [&_.ant-steps-item-tail]:!h-[110px] [&_.ant-steps-item-tail]:!w-[4px] [&_.ant-steps-item-icon]:!text-2xl [&_.ant-steps-item-icon]:!w-10 [&_.ant-steps-item-icon]:!h-10 [&_.ant-steps-item-icon]:!font-bold [&_.ant-steps-item-icon]:!flex [&_.ant-steps-item-icon]:!items-center [&_.ant-steps-item-icon]:!justify-center [&_.ant-steps-item-title]:!font-medium [&_.ant-steps-item-title]:!text-base"
          />
        </div>
        <div className="w-3/4 overflow-y-auto px-10 py-7 pb-24">{getStepContent()}</div>
      </div>
    </div>
  );
};

export default CreateCausalLoop;
